/**
 * Transport-aware wrapper for the catalog READ (`CatalogApi.list`).
 *
 * Wraps the REST `CatalogApi` so `list()` takes the fast GraphQL path when the
 * central transport registry says so (`resolveTransport("catalog.list", …)`),
 * and TRANSPARENTLY falls back to REST on any GraphQL error — so flipping the
 * flag on can never harden into an outage. Everything else on the CatalogApi
 * (create/update/remove, assignments, primaries, logos) is delegated untouched:
 * writes and un-modeled reads stay on REST.
 *
 * GraphQL is used only when ALL hold, else REST:
 *   - a GraphQL client is configured,
 *   - the registry resolves the operation to "graphql",
 *   - the kind is one GraphQL serves (entity/tag/label/group — not address),
 *   - the query uses no filter GraphQL doesn't model yet (see
 *     {@link usesUnmodeledFilter}).
 */
import type { CatalogApi, CatalogListPage, CatalogListQuery } from "@authe/catalog";
import type { CatalogItemKind } from "@authe/fe-commons/catalog";
import type { FeatureFlags } from "@authe/fe-commons/flags";
import type { GraphQLClient } from "@authe/fe-commons/graphql";
import {
  GRAPHQL_CATALOG_ID_LIMIT,
  catalogKindToGraphql,
  catalogListUnroutable,
  fetchCatalogItems,
  graphqlFetchCatalogItemsByIds,
  GraphQLRequestError,
} from "@authe/fe-commons/graphql";
import {
  resolveTransport,
  type TransportCapabilitySource,
} from "@authe/fe-commons/transport";
import { createLogger } from "@authe/fe-commons/logging";

const log = createLogger("dashboard:graphql-catalog");

export interface TransportAwareCatalogOptions {
  /** The GraphQL read client, or `null` when unconfigured (always REST). */
  graphqlClient: GraphQLClient | null;
  /** The live feature-flag set (registry precedence: master + per-op). */
  flags: FeatureFlags;
  /**
   * The caller's plan ceiling, from `<TransportCapabilitiesProvider>`.
   *
   * Passed in rather than read here because this is not a component. Omitting it
   * means unrestricted, which is the same thing the context defaults to — so a
   * host with no plan model behaves as it always did.
   */
  capabilities?: TransportCapabilitySource;
}

export function withGraphqlCatalogList(
  rest: CatalogApi,
  { graphqlClient, flags, capabilities }: TransportAwareCatalogOptions,
): CatalogApi {
  return {
    ...rest,
    async list(kind: CatalogItemKind, query: CatalogListQuery = {}): Promise<CatalogListPage> {
      // Both the kind check and the unmodeled-filter check now live beside the
      // GraphQL document (`catalogListUnroutable`), which is the thing that
      // would drop a newly added filter — this wrapper had its own copy, one app
      // away from the query it was guarding.
      const wantsGraphql =
        graphqlClient != null &&
        catalogListUnroutable(kind, query) === null &&
        resolveTransport("catalog.list", flags, {
          // The real value, not a hardcoded `true`: the registry already owns
          // "no endpoint means REST", and restating it as a constant here while
          // re-deriving it from `graphqlClient` on the line above is one rule in
          // two places, which is how the two come to disagree.
          graphqlConfigured: graphqlClient != null,
          capabilities,
        }) === "graphql";

      if (wantsGraphql && graphqlClient) {
        try {
          return await fetchCatalogItems(graphqlClient, kind, {
            search: query.search,
            limit: query.limit,
            cursor: query.cursor,
          });
        } catch (err) {
          // Transport/GraphQL failure → fall back to REST. Anything else (a
          // programming error) surfaces so it isn't silently masked.
          if (!(err instanceof GraphQLRequestError)) throw err;
          log.warn("catalog.list GraphQL failed, falling back to REST", {
            kind,
            status: err.status,
            message: err.message,
          });
        }
      }
      return rest.list(kind, query);
    },

    /**
     * Resolve rows by id — one row per GraphQL alias, and only for a SMALL set.
     *
     * The economics are the opposite of the list's: REST reads every id in a
     * single call with a comma-joined `ids`, while `catalogItem` answers one row
     * per alias. So past {@link GRAPHQL_CATALOG_ID_LIMIT} this stays on REST
     * whatever the setting says — the same rule, for the same reason, as the
     * address overlay batch. The read worth routing is the single id: a catalog
     * row opening its own page, which is a point lookup on both sides.
     *
     * Kept OPTIONAL-safe: the underlying adapter declares `getByIds` optional, so
     * a host that did not supply one must not gain a half-working method here.
     */
    getByIds: rest.getByIds
      ? async (kind: CatalogItemKind, ids: string[]) => {
          const routable =
            graphqlClient != null &&
            ids.length > 0 &&
            ids.length <= GRAPHQL_CATALOG_ID_LIMIT &&
            catalogKindToGraphql(kind) != null &&
            resolveTransport("catalog.item", flags, {
              graphqlConfigured: graphqlClient != null,
              capabilities,
            }) === "graphql";

          if (routable && graphqlClient) {
            try {
              return await graphqlFetchCatalogItemsByIds(graphqlClient, kind, ids);
            } catch (err) {
              if (!(err instanceof GraphQLRequestError)) throw err;
              log.warn("catalog.item GraphQL failed, falling back to REST", {
                kind,
                ids: ids.length,
                status: err.status,
                message: err.message,
              });
            }
          }
          return rest.getByIds!(kind, ids);
        }
      : undefined,
  };
}
